import React from 'react';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'hooks/store';
import SectionBasePage from 'components/SectionBasePage';
import { Table } from 'UI';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import {
  isLoadingProductsSelector,
  productsEntitiesSelector,
  productsIdsSelector,
} from 'state/slices/products';
import { Product } from 'constants/interface';
import { TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { PaginationInternalData } from 'hooks/pagination';
import { ProductTypeEnum, ProductTypeMapping } from 'constants/product';

import ProductListExtra from './components/ProductListExtra';
import {
  MainContainer,
  DetailsButton,
} from './style';

interface ProductListPageProps {
  pageData: PaginationInternalData
  onTableChange: (
    pageData: TablePaginationConfig,
    filterData: Record<string, FilterValue | null>,
    sortData: SorterResult<
    Omit<Product, 'variety'>> | SorterResult<Omit<Product, 'variety'>>[],
  ) => void
  onSearch(content: string): void
  setFilter(
    filterData: Record<string, (boolean | React.Key)[] | null>,
  ): void
}

const ProductListPage = ({
  pageData,
  onTableChange,
  onSearch,
  setFilter,
}: ProductListPageProps): JSX.Element => {
  const productsEntites = useSelector(productsEntitiesSelector);
  const productsIds = useSelector(productsIdsSelector);
  const isLoadingProducts = useSelector(isLoadingProductsSelector);
  const { url } = useRouteMatch();
  const breadcrumbNavigator = useBreadcrumbNavigation();

  const products = productsIds.map((productId) => {
    const entity = productsEntites[productId];
    if (!entity) return undefined;
    const tableProduct: Omit<Product, 'variety'> & {
      key?: string,
      culture?: string
      variety?: string
    } = {
      ...entity,
      culture: entity.variety?.culture?.name,
      variety: entity.variety?.name,
      key: productId.toString(),
    };
    return tableProduct;
  }).filter((tableProduct): tableProduct is Omit<Product, 'variety'> => !!tableProduct);

  const sorter = (): number => 0;

  const onActiveToggle = (checked: boolean) => setFilter({
    isActive: [!checked],
  });

  // const isActiveFilter = pageData.filter.isActive ?? null;
  const typeFilter = pageData.filter.type ?? null;

  return (
    <SectionBasePage title="Produtos" extra={<ProductListExtra onSearch={onSearch} onActiveToggle={onActiveToggle} />}>
      <MainContainer>
        <Table
          dataSource={products}
          loading={isLoadingProducts}
          pagination={pageData}
          showSorterTooltip={false}
          locale={{
            filterReset: 'Limpar',
            cancelSort: 'Clique para limpar ordenação',
            triggerAsc: 'Clique para ordem crescente',
            triggerDesc: 'Clique para ordem decrescente',
          }}
          onChange={onTableChange}
        >
          <Table.Column
            title="Cultura"
            key="culture"
            dataIndex="culture"
            sorter={sorter}
            sortOrder={pageData.orderBy === 'culture' ? pageData.order : undefined}
          />
          <Table.Column
            title="Variedade"
            key="variety"
            dataIndex="variety"
            sorter={sorter}
            sortOrder={pageData.orderBy === 'variety' ? pageData.order : undefined}
          />
          <Table.Column
            title="Formação"
            key="type"
            dataIndex="type"
            render={(type: ProductTypeEnum) => (
              <span>{ProductTypeMapping[type]}</span>
            )}
            filters={[{
              text: ProductTypeMapping[ProductTypeEnum.STANDARD],
              value: ProductTypeEnum.STANDARD,
            },
            {
              text: ProductTypeMapping[ProductTypeEnum.SCION],
              value: ProductTypeEnum.SCION,
            },
            {
              text: ProductTypeMapping[ProductTypeEnum.STOCK],
              value: ProductTypeEnum.STOCK,
            }]}
            filterMultiple={false}
            filteredValue={typeFilter !== null ? [typeFilter] : null}
          />
          <Table.Column
            title="Dias/Form"
            key="periodFormation"
            dataIndex="periodFormation"
            render={(periodFormation) => (
              <span>{periodFormation ? `${periodFormation} dias` : '-'}</span>
            )}
            sorter={sorter}
            sortOrder={pageData.orderBy === 'periodFormation' ? pageData.order : undefined}
          />
          <Table.Column
            title="Dias/Enx"
            key="periodGrafting"
            dataIndex="periodGrafting"
            render={(periodGrafting) => (
              <span>{periodGrafting ? `${periodGrafting} dias` : '-'}</span>
            )}
            sorter={sorter}
            sortOrder={pageData.orderBy === 'periodGrafting' ? pageData.order : undefined}
          />
          <Table.Column
            title="Perda"
            key="loss"
            dataIndex="loss"
            render={(loss) => (
              <span>{loss ? `${(loss * 100).toFixed(0)}%` : '-'}</span>
            )}
            sorter={sorter}
            sortOrder={pageData.orderBy === 'loss' ? pageData.order : undefined}
          />
          <Table.Column
            title=""
            key="details"
            dataIndex="id"
            render={(productId) => (
              <DetailsButton onClick={() => breadcrumbNavigator.push({
                label: `${productsEntites[productId]?.variety?.culture?.name} ${productsEntites[productId]?.variety?.name}`,
                url: `${url}/${productsEntites[productId]?.variety?.id}`,
              })}
              >
                abrir »
              </DetailsButton>
            )}
          />
        </Table>
      </MainContainer>
    </SectionBasePage>
  );
};

export default ProductListPage;
